import { useMemo } from "react";
import { CalendarPlus, CalendarRange } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/empty-state";
import { dateKey, formatDateLong, isToday } from "@/lib/date";
import { AppointmentCard } from "@/features/appointment/components/appointment-card";
import type { Appointment } from "@/features/appointment/types";
import type { Lead } from "@/features/lead/types";
import type { BrokerSummary } from "@/features/appointment/types";

interface AppointmentAgendaViewProps {
  appointments: Appointment[];
  leads: Lead[];
  brokers: BrokerSummary[];
  onSelectAppointment: (appointment: Appointment) => void;
  onCreate: () => void;
}

interface DayGroup {
  key: string;
  date: Date;
  appointments: Appointment[];
}

/** Group appointments by their scheduled day, ordered chronologically. */
function groupByDay(appointments: Appointment[]): DayGroup[] {
  const groups = new Map<string, DayGroup>();
  for (const appointment of appointments) {
    const key = dateKey(appointment.scheduledAt);
    if (key === "") continue;
    const existing = groups.get(key);
    if (existing) {
      existing.appointments.push(appointment);
    } else {
      groups.set(key, {
        key,
        date: new Date(appointment.scheduledAt),
        appointments: [appointment],
      });
    }
  }
  return Array.from(groups.values()).sort((a, b) =>
    a.key.localeCompare(b.key),
  );
}

export function AppointmentAgendaView({
  appointments,
  leads,
  brokers,
  onSelectAppointment,
  onCreate,
}: AppointmentAgendaViewProps) {
  const groups = useMemo(() => groupByDay(appointments), [appointments]);
  const leadById = useMemo(
    () => new Map(leads.map((l) => [l.id, l])),
    [leads],
  );
  const brokerById = useMemo(
    () => new Map(brokers.map((b) => [b.id, b])),
    [brokers],
  );

  if (groups.length === 0) {
    return (
      <EmptyState
        icon={<CalendarRange aria-hidden="true" className="size-10" />}
        title="Chưa có lịch hẹn nào"
        description="Gửi yêu cầu xem nhà đầu tiên để bắt đầu theo dõi."
        action={
          <Button onClick={onCreate}>
            <CalendarPlus aria-hidden="true" />
            Yêu cầu xem nhà
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="appointment-agenda">
      {groups.map((group) => (
        <section key={group.key} className="flex flex-col gap-2">
          <h2
            className="sticky top-0 z-10 -mx-1 flex items-center gap-2 bg-background/95 px-1 py-2 text-sm font-semibold backdrop-blur"
            data-testid={`agenda-day-${group.key}`}
          >
            <span className="tracking-tight">{formatDateLong(group.date)}</span>
            <span className="font-mono text-[11px] font-normal text-ink-faint tabular-nums">
              {group.appointments.length}
            </span>
            {isToday(group.date) ? (
              <span className="inline-flex items-center rounded-full bg-accent px-2 py-0.5 font-mono text-[11px] font-semibold text-accent-foreground">
                Hôm nay
              </span>
            ) : null}
          </h2>

          <div className="flex flex-col gap-2">
            {group.appointments.map((appointment) => {
              const lead =
                appointment.leadId !== null
                  ? leadById.get(appointment.leadId)
                  : undefined;
              const broker = brokerById.get(appointment.brokerId);
              return (
                <AppointmentCard
                  key={appointment.id}
                  appointment={appointment}
                  lead={
                    lead
                      ? {
                          fullName: lead.customer.fullName,
                        }
                      : undefined
                  }
                  property={
                    lead && lead.interestedPropertyBuildingName !== null
                      ? { buildingName: lead.interestedPropertyBuildingName }
                      : undefined
                  }
                  broker={
                    broker ? { fullName: broker.fullName } : undefined
                  }
                  onClick={onSelectAppointment}
                />
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
